import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Select, Button, Steps, message, Alert, Spin, InputNumber } from 'antd';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { getFarmsApi } from '../../api/farms';
import { createPlotApi } from '../../api/crops';
import { AREA_UNITS } from '../../utils/constants';
import DrawControl from './DrawControl';
import 'leaflet/dist/leaflet.css';
import dayjs from 'dayjs';

const DEFAULT_CENTER = [23.0225, 72.5714];

const SQ_METER_FACTOR = {
  '1': 1 / 101.171,   // Guntha
  '2': 1 / 2529.29,   // Bigha
  '3': 1 / 4046.86,   // Acre
  '4': 1 / 10000,     // Hectare
  '5': 10.7639,        // Sq Feet
  '6': 1,             // Sq Meter
};

function toUnit(sqMeters, unitKey) {
  return parseFloat((sqMeters * (SQ_METER_FACTOR[unitKey] ?? SQ_METER_FACTOR['3'])).toFixed(4));
}

async function geocodeLocation(locationStr) {
  if (!locationStr) return null;
  // Location stored as "lat, lng" — parse directly
  const parts = locationStr.split(',').map((s) => parseFloat(s.trim()));
  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
    return [parts[0], parts[1]];
  }
  // Fallback: Nominatim search for place names
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationStr)}&format=json&limit=1`,
      { headers: { 'User-Agent': 'KhetiHisaab/1.0', 'Accept-Language': 'en' } }
    );
    const data = await res.json();
    if (data.length > 0) return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
  } catch {}
  return null;
}

function FlyToLocation({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, 15, { animate: true, duration: 1.2 });
  }, [center, map]);
  return null;
}

export default function AddPlotModal({ open, onClose, onSuccess, defaultFarmId, defaultFarmLocation, initialPlot }) {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [farms, setFarms] = useState([]);
  const [farmsLoading, setFarmsLoading] = useState(false);
  const [plotGeoJson, setPlotGeoJson] = useState(null);
  const [existingPath, setExistingPath] = useState(null);
  const [areaSqMeters, setAreaSqMeters] = useState(null);
  const [areaUnit, setAreaUnit] = useState('3');
  const [areaValue, setAreaValue] = useState(null);
  const [farmCenter, setFarmCenter] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPlotGeoJson(null);
    setExistingPath(null);
    setAreaSqMeters(null);
    setAreaValue(null);
    setAreaUnit('3');
    setFarmCenter(null);
    form.resetFields();

    if (initialPlot) {
      form.setFieldsValue({ farm_id: initialPlot.farm_id, plot_name: initialPlot.name });
      const unit = String(initialPlot.seted_area_unit || '3');
      setAreaUnit(unit);
      setAreaValue(initialPlot.seted_area ? parseFloat(initialPlot.seted_area) : null);
      setExistingPath(initialPlot.path || null);
      // Center map on existing polygon centroid
      try {
        const points = typeof initialPlot.path === 'string' ? JSON.parse(initialPlot.path) : initialPlot.path;
        if (points?.length >= 3) {
          const lat = points.reduce((s, p) => s + p.latitude, 0) / points.length;
          const lng = points.reduce((s, p) => s + p.longitude, 0) / points.length;
          setFarmCenter([lat, lng]);
        }
      } catch {}
    } else if (defaultFarmLocation) {
      geocodeLocation(defaultFarmLocation).then((coords) => {
        if (coords) setFarmCenter(coords);
      });
    }

    setFarmsLoading(true);
    const currentYear = String(dayjs().year());
    getFarmsApi({ years: [currentYear], cropStatus: '0' })
      .then((res) => {
        const list = res.data?.data?.farms || res.data?.farms || res.data || [];
        setFarms(list);
        const farmId = initialPlot?.farm_id || defaultFarmId;
        if (farmId) {
          form.setFieldsValue({ farm_id: farmId });
          if (!initialPlot && !defaultFarmLocation) {
            const farm = list.find((f) => String(f.id) === String(farmId));
            if (farm?.location) {
              geocodeLocation(farm.location).then((coords) => {
                if (coords) setFarmCenter(coords);
              });
            }
          }
        }
      })
      .catch(() => {})
      .finally(() => setFarmsLoading(false));
  }, [open, form, defaultFarmId, defaultFarmLocation, initialPlot]);

  const handleFarmChange = useCallback(async (farmId) => {
    const farm = farms.find((f) => f.id === farmId);
    if (!farm?.location) return;
    setGeocoding(true);
    const coords = await geocodeLocation(farm.location);
    if (coords) setFarmCenter(coords);
    setGeocoding(false);
  }, [farms]);

  const handlePolygonCreated = useCallback((geoJson, sqm) => {
    setPlotGeoJson(geoJson);
    setAreaSqMeters(sqm);
    setAreaValue(toUnit(sqm, areaUnit));
  }, [areaUnit]);

  // When unit changes, recalculate area from stored sqm
  const handleUnitChange = useCallback((unit) => {
    setAreaUnit(unit);
    if (areaSqMeters != null) {
      setAreaValue(toUnit(areaSqMeters, unit));
    }
  }, [areaSqMeters]);

  const handleNext = async () => {
    try {
      await form.validateFields(['farm_id', 'plot_name']);
      setStep(1);
    } catch {}
  };

  const handleSave = async () => {
    if (!plotGeoJson && !existingPath) {
      message.warning('Please draw the plot boundary on the map first');
      return;
    }
    try {
      const values = form.getFieldsValue(true);
      let plotPath;
      if (plotGeoJson) {
        const coords = plotGeoJson.geometry?.coordinates?.[0] ?? [];
        plotPath = coords.map(([lng, lat]) => ({ longitude: lng, latitude: lat }));
      } else {
        const points = typeof existingPath === 'string' ? JSON.parse(existingPath) : existingPath;
        plotPath = points;
      }
      setSaving(true);
      await createPlotApi({
        id: initialPlot?.id ?? null,
        plotPath,
        farm: values.farm_id,
        plotName: values.plot_name,
        sqmArea: areaSqMeters != null ? parseFloat(areaSqMeters).toFixed(3) : '0',
        area: areaValue != null ? parseFloat(areaValue).toFixed(3) : '0',
        unit: areaUnit,
      });
      message.success('Plot saved!');
      onSuccess?.();
      onClose();
    } catch {
      message.error('Failed to save plot');
    } finally {
      setSaving(false);
    }
  };

  const footer = [
    <Button key="cancel" onClick={onClose}>Cancel</Button>,
    step === 1 && <Button key="back" onClick={() => setStep(0)}>Back</Button>,
    step === 0
      ? <Button key="next" type="primary" onClick={handleNext}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>
          Next: Draw Boundary
        </Button>
      : <Button key="save" type="primary" loading={saving} onClick={handleSave}
          disabled={!plotGeoJson}
          style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>
          Save Plot
        </Button>,
  ];

  return (
    <Modal
      title={initialPlot ? 'Edit Plot' : 'Add Plot'}
      open={open}
      onCancel={onClose}
      width={840}
      footer={footer}
      destroyOnClose
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 24 }}
        items={[{ title: 'Farm & Details' }, { title: 'Draw Boundary & Area' }]}
      />

      {/* ── Step 0: farm + plot name ── */}
      {step === 0 && (
        <Form form={form} layout="vertical" size="large">
          <Form.Item label="Farm" name="farm_id" rules={[{ required: true, message: 'Select a farm' }]}>
            <Select
              placeholder={farmsLoading ? 'Loading farms...' : 'Select farm...'}
              loading={farmsLoading}
              options={farms.map((f) => ({ value: f.id, label: f.name }))}
              onChange={handleFarmChange}
              disabled={!!defaultFarmId}
              showSearch
              filterOption={(input, opt) => opt.label.toLowerCase().includes(input.toLowerCase())}
              suffixIcon={geocoding ? <Spin size="small" /> : undefined}
            />
          </Form.Item>
          {geocoding && (
            <div style={{ color: '#1a6b2a', fontSize: 12, marginTop: -12, marginBottom: 12 }}>
              📍 Finding farm location on map...
            </div>
          )}
          <Form.Item label="Plot Name" name="plot_name" rules={[{ required: true, message: 'Enter plot name' }]}>
            <Input placeholder="e.g. North Field, River Plot" />
          </Form.Item>
        </Form>
      )}

      {/* ── Step 1: map + area below ── */}
      {step === 1 && (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Click the polygon tool (left toolbar). Add 3+ corner points, then double-click to finish. Area is auto-calculated below."
          />

          <div style={{ height: 400, borderRadius: 8, overflow: 'hidden', border: '1px solid #d9d9d9' }}>
            <MapContainer
              center={farmCenter || DEFAULT_CENTER}
              zoom={farmCenter ? 15 : 14}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Esri"
              />
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" opacity={0} />
              <FlyToLocation center={farmCenter} />
              <DrawControl onCreated={handlePolygonCreated} existingPath={existingPath} />
            </MapContainer>
          </div>

          {/* Area row — always shown, populated after drawing */}
          <div style={{
            marginTop: 16,
            padding: '12px 16px',
            background: plotGeoJson ? '#f6ffed' : '#fafafa',
            border: `1px solid ${plotGeoJson ? '#b7eb8f' : '#d9d9d9'}`,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <span style={{ fontWeight: 500, minWidth: 80, color: '#595959' }}>
              {plotGeoJson ? '📐 Plot Area:' : '📐 Area:'}
            </span>
            <InputNumber
              value={areaValue}
              onChange={setAreaValue}
              min={0}
              precision={4}
              placeholder="Draw boundary to calculate"
              style={{ width: 160 }}
              disabled={!plotGeoJson}
              addonAfter={
                <Select
                  value={areaUnit}
                  onChange={handleUnitChange}
                  options={Object.entries(AREA_UNITS).map(([k, v]) => ({ value: k, label: v }))}
                  style={{ width: 100 }}
                />
              }
            />
            {plotGeoJson && areaSqMeters != null && (
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                ({(areaSqMeters).toFixed(1)} m² · {plotGeoJson.geometry.coordinates[0].length - 1} points)
              </span>
            )}
            {!plotGeoJson && (
              <span style={{ color: '#bfbfbf', fontSize: 12 }}>Draw the boundary first</span>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
